<script lang="ts" setup>
import { getFormattedYearRange } from '~/composables/get-formatted-date-range'

const props = defineProps<{
  data: any
}>()

const { t } = useI18n()

const years = [props?.data?.yearOfBirth, props?.data?.yearOfDeath].filter(Boolean)
const nationalities = props?.data?.nationalities?.map((n: any) => {
  const isPlaceOfBirthLabel = n.isPlaceOfBirth && props?.data?.nationalities?.length > 1 ? t('labels.placeOfBirth') : ''
  return [n.municipality, n.country].filter(Boolean).join(', ') + isPlaceOfBirthLabel
})

// The person's roles over time, in one list. Two sources, because the site
// models them differently: a permanent role ("vast") is a recurrence on the
// person, a guest year is an entry in a guest period that points back here.
// Shown together, the four kinds of tutor read the way the client describes
// them -- guest only, guest then permanent, permanent then guest again.
const time = (date: any) => {
  const parsed = date ? new Date(date).getTime() : Number.NaN
  return Number.isNaN(parsed) ? null : parsed
}

const periods = computed(() => [
  ...(props?.data?.recurrences ?? []).map((recurrence: any) => ({
    key: recurrence.id,
    label: t(`labels.${recurrence.type}`),
    // years only, the way the people page labels its groups
    range: getFormattedYearRange(recurrence.dateStart, recurrence.dateEnd, {
      isOngoing: Boolean(recurrence.isOngoing),
    }),
    rooms: recurrence.rooms ?? [],
    // an ongoing role has no end, so it outranks every closed one
    sortEnd: recurrence.isOngoing
      ? Number.POSITIVE_INFINITY
      : time(recurrence.dateEnd) ?? time(recurrence.dateStart) ?? 0,
    sortStart: time(recurrence.dateStart) ?? 0,
  })),
  ...(props?.data?.guestAppearances?.docs ?? []).map((guest: any) => ({
    key: `guest-${guest.id}`,
    label: t('labels.guest'),
    range: getFormattedYearRange(guest.dateStart, guest.dateEnd),
    rooms: [],
    sortEnd: time(guest.dateEnd) ?? time(guest.dateStart) ?? 0,
    sortStart: time(guest.dateStart) ?? 0,
  })),
]
  .filter(period => period.range)
  // current first, then most recently finished
  .sort((a, b) => (b.sortEnd - a.sortEnd) || (b.sortStart - a.sortStart)))

</script>

<template>
  <div class="flex flex-col gap-1">
    <ul
      v-if="periods.length"
    >
      <li
        v-for="period in periods"
        :key="period.key"
        class="text-d"
      >
        {{ period.label }}:
        {{ period.range }}
        <template v-if="period.rooms.length">
          (<template
            v-for="(room, index) in period.rooms"
            :key="room.id"
          ><template v-if="index > 0">, </template><site-link
            :route-object="{
              name: 'rooms-slug',
              params: {
                slug: room.slug
              }
            }"
            class="!inline text-d text-hover-accent"
          >{{ room.title }}</site-link></template>)
        </template>
      </li>
    </ul>

    <ul
      v-if="data?.programmes?.docs?.length"
      class="text-d"
    >
      <li>
        {{ $t('labels.relatedEvent', { count: data?.programmes?.docs?.length }) }}
      </li>
      <li
        v-for="(programme, index) in data.programmes.docs"
        :key="index"
        class="not-last:mb-0_35"
      >
        <site-link
          :route-object="{
            name: 'programme-type-slug',
            params: {
              type: programme.type?.slug,
              slug: programme.slug
            }
          }"
          class="text-d text-hover-accent"
        >
          {{ programme.title }}
        </site-link>

        <ul
          v-if="programme.recurrences?.length"
          class="text-d"
        >
          <li
            v-for="(recurrence, index) in getFormattedRecurrences(programme.recurrences)"
            :key="index"
          >
            {{ recurrence }}
          </li>
        </ul>
      </li>
    </ul>

    <div
      v-if="hasRichTextContent(data?.description)"
      class="container-d"
    >
      <core-rich-text
        :content="data.description"
      />
    </div>

    <core-media-preview
      v-if="data?.images?.length"
      :media="data?.images"
      :limit="8"
    />
  </div>
</template>